// Pure, stateless polygon-geometry validation for geofence editing (MOOV-5222).
//
// Operates on an OPEN ring `fence`: a flat array of [lat, lng] pairs where the
// first point is NOT repeated at the end (Leaflet does not repeat it). The
// implicit closing segment goes from the last vertex back to the first.
//
// No i18n and no third-party dependency here: the orchestrator returns stable
// error codes that the consuming component maps to translated messages.

// Tolerance for treating two coordinates as coincident / segments as collinear.
// Hand-drawn geofences never place vertices this close on purpose.
const EPSILON = 1e-9

// A ring whose enclosed area is this small relative to its bounding box is
// treated as collinear (zero-area). Exact collinear rings score ~0; genuinely
// thin-but-valid polygons score far above this, so no false positives.
const AREA_RATIO_EPSILON = 1e-9

export const GEOMETRY_ERRORS = {
  SELF_INTERSECTION: 'SELF_INTERSECTION',
  TOO_FEW_VERTICES: 'TOO_FEW_VERTICES',
  ZERO_AREA: 'ZERO_AREA'
}

// Cross product of vectors (b - a) x (c - a). Sign gives orientation of a→b→c.
function cross (a, b, c) {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
}

function onSegment (a, b, p) {
  return Math.min(a[0], b[0]) - EPSILON <= p[0] && p[0] <= Math.max(a[0], b[0]) + EPSILON &&
    Math.min(a[1], b[1]) - EPSILON <= p[1] && p[1] <= Math.max(a[1], b[1]) + EPSILON
}

// True when segment p1p2 properly intersects segment p3p4 (including collinear
// overlap and touching endpoints).
export function segmentsIntersect (p1, p2, p3, p4) {
  const d1 = cross(p3, p4, p1)
  const d2 = cross(p3, p4, p2)
  const d3 = cross(p1, p2, p3)
  const d4 = cross(p1, p2, p4)

  if (((d1 > EPSILON && d2 < -EPSILON) || (d1 < -EPSILON && d2 > EPSILON)) &&
      ((d3 > EPSILON && d4 < -EPSILON) || (d3 < -EPSILON && d4 > EPSILON))) {
    return true
  }

  // Collinear / touching cases.
  if (Math.abs(d1) <= EPSILON && onSegment(p3, p4, p1)) return true
  if (Math.abs(d2) <= EPSILON && onSegment(p3, p4, p2)) return true
  if (Math.abs(d3) <= EPSILON && onSegment(p1, p2, p3)) return true
  if (Math.abs(d4) <= EPSILON && onSegment(p1, p2, p4)) return true

  return false
}

// True when any pair of non-adjacent edges of the open ring cross each other.
// The closing edge (last→first) is included. O(n^2), fine for hand-drawn zones.
export function hasSelfIntersection (fence) {
  if (!Array.isArray(fence) || fence.length < 4) return false
  const n = fence.length
  for (let i = 0; i < n; i++) {
    const a1 = fence[i]
    const a2 = fence[(i + 1) % n]
    for (let j = i + 1; j < n; j++) {
      // Skip edges that share a vertex (adjacent edges, and the pair that wraps
      // around the closing segment) — touching at a shared vertex is not a cross.
      if (j === i || j === (i + 1) % n || (j + 1) % n === i) continue
      const b1 = fence[j]
      const b2 = fence[(j + 1) % n]
      if (segmentsIntersect(a1, a2, b1, b2)) return true
    }
  }
  return false
}

// Number of distinct vertices, collapsing points that coincide within EPSILON.
export function distinctVertexCount (fence) {
  if (!Array.isArray(fence)) return 0
  const kept = []
  for (const p of fence) {
    if (!kept.some(q => Math.abs(q[0] - p[0]) <= EPSILON && Math.abs(q[1] - p[1]) <= EPSILON)) {
      kept.push(p)
    }
  }
  return kept.length
}

// Signed area via the shoelace formula, computed in GeoJSON axis order
// (x = lng, y = lat) even though `fence` stores [lat, lng]. Positive area means
// counter-clockwise (CCW), i.e. a valid GeoJSON exterior ring (RFC 7946).
export function signedArea (fence) {
  if (!Array.isArray(fence) || fence.length < 3) return 0
  let sum = 0
  const n = fence.length
  for (let i = 0; i < n; i++) {
    const [latA, lngA] = fence[i]
    const [latB, lngB] = fence[(i + 1) % n]
    sum += (lngA * latB - lngB * latA)
  }
  return sum / 2
}

// A ring is clockwise when its signed (CCW-positive) area is negative.
export function isClockwise (fence) {
  return signedArea(fence) < 0
}

// True when a ring with 3+ distinct vertices still encloses no area because all
// its vertices are collinear — a horizontal line (same lat), a vertical line
// (same lng), or a diagonal line. The backend rejects such degenerate polygons.
// Scale-robust: axis-aligned lines collapse one bounding-box dimension; other
// collinear sets have area negligible relative to their bounding box.
export function hasZeroArea (fence) {
  if (!Array.isArray(fence) || fence.length < 3) return false
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (const [lat, lng] of fence) {
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
  }
  const latRange = maxLat - minLat
  const lngRange = maxLng - minLng
  // Horizontal (same lat) or vertical (same lng) line.
  if (latRange <= EPSILON || lngRange <= EPSILON) return true
  // Diagonal collinear: enclosed area negligible vs. its bounding-box area.
  return Math.abs(signedArea(fence)) / (latRange * lngRange) < AREA_RATIO_EPSILON
}

// Return the ring wound to the requested orientation, reversing a copy when it
// differs. GeoJSON exterior rings are CCW, so callers pass desiredClockwise=false.
// Never mutates the input.
export function ensureWinding (fence, desiredClockwise = false) {
  if (!Array.isArray(fence) || fence.length < 3) return fence
  return isClockwise(fence) === desiredClockwise ? fence : fence.slice().reverse()
}

// Orchestrator: return the list of BLOCKING geometry error codes for a ring.
// Winding is intentionally NOT reported here — it is auto-corrected at save time.
export function validateGeofencePolygon (fence) {
  const errors = []
  // Degenerate cases short-circuit: a line has no meaningful "crossing edges".
  if (distinctVertexCount(fence) < 3) {
    errors.push(GEOMETRY_ERRORS.TOO_FEW_VERTICES)
    return errors
  }
  if (hasZeroArea(fence)) {
    errors.push(GEOMETRY_ERRORS.ZERO_AREA)
    return errors
  }
  if (hasSelfIntersection(fence)) errors.push(GEOMETRY_ERRORS.SELF_INTERSECTION)
  return errors
}
